import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { AddressConflict, CodeConflict } from '@/hooks/chain/useReferralRegistrationPreflight'

export interface RegisterReferrerCardProps {
    address: string
    code: string
    override: string
    attestKyc: boolean
    attestGeo: boolean
    attestTerms: boolean
    defaultShareBps: number | undefined
    addressConflict: AddressConflict | null
    codeConflict: CodeConflict | null
    canRegister: boolean
    onAddressChange: (value: string) => void
    onCodeChange: (value: string) => void
    onOverrideChange: (value: string) => void
    onAttestKycChange: (value: boolean) => void
    onAttestGeoChange: (value: boolean) => void
    onAttestTermsChange: (value: boolean) => void
    onRegister: () => void
}

// Controlled registration form. The attestation checklist encodes the design rule that
// the on-chain registration IS the attestation that off-chain vetting completed; the
// conflict notices are the debounced pre-submit checks.
export function RegisterReferrerCard({
    address,
    code,
    override,
    attestKyc,
    attestGeo,
    attestTerms,
    defaultShareBps,
    addressConflict,
    codeConflict,
    canRegister,
    onAddressChange,
    onCodeChange,
    onOverrideChange,
    onAttestKycChange,
    onAttestGeoChange,
    onAttestTermsChange,
    onRegister,
}: RegisterReferrerCardProps) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Register referrer</CardTitle>
                <CardDescription>
                    Registration is the on-chain attestation that off-chain vetting is complete. The
                    canonical code is permanent and case-insensitive (3–32 characters,
                    letters/digits/underscore).
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1">
                        <Label htmlFor="reg-address">Wallet address</Label>
                        <Input
                            id="reg-address"
                            placeholder="0x…"
                            value={address}
                            onChange={(e) => onAddressChange(e.target.value)}
                        />
                        {addressConflict && (
                            <p
                                data-testid="reg-address-conflict"
                                className="text-destructive text-xs"
                            >
                                This address is already registered as{' '}
                                <span className="font-mono">{addressConflict.code}</span>.
                            </p>
                        )}
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="reg-code">Referral code</Label>
                        <Input
                            id="reg-code"
                            placeholder="CRYPTOJOE"
                            value={code}
                            onChange={(e) => onCodeChange(e.target.value)}
                        />
                        {codeConflict && (
                            <p data-testid="reg-code-conflict" className="text-destructive text-xs">
                                This code is already taken by{' '}
                                <span className="font-mono">{codeConflict.owner}</span>.
                            </p>
                        )}
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="reg-override">Rate override (bps, optional)</Label>
                        <Input
                            id="reg-override"
                            placeholder={`default ${defaultShareBps ?? '—'}`}
                            value={override}
                            onChange={(e) => onOverrideChange(e.target.value)}
                        />
                    </div>
                </div>
                <div className="space-y-2 text-sm">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={attestKyc}
                            onChange={(e) => onAttestKycChange(e.target.checked)}
                        />
                        KYC completed and verified for this payee
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={attestGeo}
                            onChange={(e) => onAttestGeoChange(e.target.checked)}
                        />
                        Geo restrictions vetted (residence and promotion territories)
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={attestTerms}
                            onChange={(e) => onAttestTermsChange(e.target.checked)}
                        />
                        Referral terms signed (prohibited-claims annex included)
                    </label>
                </div>
                <Button onClick={onRegister} disabled={!canRegister}>
                    Register referrer
                </Button>
            </CardContent>
        </Card>
    )
}
